import equal from "fast-deep-equal";
import { action, computed, makeObservable, observable, transaction } from "mobx";

import SchemaParameterStore, {
  type ISchemaParameter,
} from "./SchemaParameter/schema-parameter-store";
import type { SchemaEditorMode } from "./types";
import {
  convertJsonToParameter,
  mergePreservedRootSchemaFields,
  schemaPropertyUsesComposition,
  SCHEMA_EDITOR_PRESET_FIELD_KEYS,
  stripDummyFields,
  validatePresetFields,
} from "./utils";
import { combineValidators, validateIdentifier, validateRequired } from "./validation";

import type { JSONSchema, TemplateFormat } from "@/types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonSchemaRecord = Record<string, any>;

interface BaseSchema {
  name: string;
  description: string;
  strict: boolean;
  additionalProperties: boolean;
}

interface FunctionJSONSchema extends BaseSchema {
  parameters?: JSONSchema;
}

interface SchemaJSONSchema extends BaseSchema {
  schema?: JSONSchema;
  input_schema?: JSONSchema;
}

class SchemaEditorStore {
  @observable mode: SchemaEditorMode = "interactive";
  @observable name = "";
  @observable description = "";
  @observable strict = false;
  @observable allowAdditionalProperties = false;
  @observable schemaParameters: ISchemaParameter[] = [];
  @observable initialJSONSchema: JSONSchema = {};
  @observable isStandardSchema = true;
  private _readonly = false;
  private _isFunction = false;
  @observable errors: Record<string, string> = {};
  @observable isSubmitAttempted = false;
  @observable private _format: TemplateFormat | undefined;
  @observable $defs?: ISchemaParameter[];
  @observable schemaError?: string;

  constructor(
    initialJSONSchema: FunctionJSONSchema | SchemaJSONSchema | JSONSchema,
    isFunction = false,
    readonly = false,
    format?: TemplateFormat,
  ) {
    makeObservable(this);
    this._readonly = readonly;
    this._isFunction = isFunction;
    this._format = format;

    let jsonSchema: JSONSchema = {};

    if (
      "parameters" in initialJSONSchema ||
      "schema" in initialJSONSchema ||
      "input_schema" in initialJSONSchema
    ) {
      this.name = initialJSONSchema?.name || "";
      this.description = initialJSONSchema?.description || "";
      this.strict = initialJSONSchema?.strict || false;
      this.allowAdditionalProperties = initialJSONSchema?.additionalProperties || false;
      if ("parameters" in initialJSONSchema) {
        jsonSchema = initialJSONSchema?.parameters || {};
      } else if ("schema" in initialJSONSchema || "input_schema" in initialJSONSchema) {
        jsonSchema = initialJSONSchema.input_schema || initialJSONSchema.schema || {};
      }
    } else {
      jsonSchema = structuredClone(initialJSONSchema);
    }

    // Structured output schemas are often saved as a bare JSON Schema without `name`; submission requires one.
    if (!this._isFunction && !this.name.trim()) {
      const titled =
        typeof (jsonSchema as { title?: unknown }).title === "string"
          ? (jsonSchema as { title: string }).title.trim()
          : "";
      this.name = titled.slice(0, 64) || "structured_output";
    }

    this.initialJSONSchema = jsonSchema;
    this.applyJsonToState(this.initialJSONSchema);

    // Normalize schemas for comparison - empty/missing fields are treated as equivalent
    const normalizeSchema = (obj: unknown): unknown => {
      if (obj === null || typeof obj !== "object") {
        return obj;
      }
      if (Array.isArray(obj)) {
        return obj.map(normalizeSchema);
      }

      const record = obj as Record<string, unknown>;

      // Canonicalize the legacy `nullable: true` property form to the JSON Schema
      // union-type form `type: [X, "null"]` so that both input shapes compare equal
      // to the editor's canonical output.
      let source = record;
      if (record["nullable"] === true) {
        const typeValue = record["type"];
        if (typeof typeValue === "string" && typeValue !== "null") {
          source = { ...record, type: [typeValue, "null"] };
          delete source["nullable"];
        } else if (Array.isArray(typeValue)) {
          source = { ...record };
          delete source["nullable"];
          if (!(typeValue as unknown[]).includes("null")) {
            source["type"] = [...(typeValue as unknown[]), "null"];
          }
        }
      }

      const result: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(source)) {
        // Required is a set: skip when empty, sort for order-insensitive comparison.
        if (key === "required" && Array.isArray(value)) {
          if (value.length === 0) {
            continue;
          }
          result[key] = [...value].sort();
          continue;
        }
        // Skip empty descriptions - they're semantically equivalent to not having the field
        if (key === "description" && value === "") {
          continue;
        }
        // Skip empty titles - they're semantically equivalent to not having the field
        if (key === "title" && value === "") {
          continue;
        }
        // Skip additionalProperties: false - it's the default for strict schemas
        if (key === "additionalProperties" && value === false) {
          continue;
        }
        result[key] = normalizeSchema(value);
      }
      return result;
    };

    if (
      jsonSchema &&
      !equal(normalizeSchema(this.definition), normalizeSchema(this.initialJSONSchema)) &&
      !schemaPropertyUsesComposition(jsonSchema)
    ) {
      this.isStandardSchema = false;
      this.applyJsonToState(this.initialJSONSchema, false);
    }
  }

  @action
  validate(): boolean {
    const nameValidator = this._isFunction
      ? (value: string | undefined) => validateIdentifier(value, "Name")
      : (value: string | undefined) => validateRequired(value, "Name");
    const result = combineValidators({ name: this.name }, { name: nameValidator });

    this.errors = result.errors;

    const paramResults = this.schemaParameters.map(param => param.validate());
    const parametersValid = paramResults.every(res => res === true);

    return result.isValid && parametersValid;
  }

  get shouldShowFormatWarning() {
    const containsEnumVariable = this.schemaParameters.some(param => param.isEnumVariable);

    return this._format !== "jinja2" && (containsEnumVariable || this.containsJinja);
  }

  get format() {
    return this._format;
  }

  @action setFormat(format: TemplateFormat) {
    this._format = format;
  }

  get canSubmit(): boolean {
    return this.validate();
  }

  @action
  markSubmitAttempted() {
    this.isSubmitAttempted = true;
    this.schemaParameters.forEach(param => param.markSubmitAttempted());
  }

  @action setAutomatic(value: boolean) {
    transaction(() => {
      if (value) {
        this.applyJsonToState(this.initialJSONSchema, true);
      } else {
        this.isStandardSchema = false;
        this.applyJsonToState(this.initialJSONSchema, false);
      }
    });
  }

  get isFunction() {
    return this._isFunction;
  }

  get isReadonly() {
    return this._readonly;
  }

  @action
  deleteParameter(parameterToDelete: ISchemaParameter) {
    // Handle top-level parameters
    this.schemaParameters = this.schemaParameters.filter(param => param !== parameterToDelete);

    this.schemaParameters.forEach(parameter => {
      parameter._required = parameter._required.filter(
        requiredParam => requiredParam !== parameterToDelete,
      );
    });

    // Handle nested parameters
    this.schemaParameters.forEach(param => {
      this.deleteNestedParameter(param, parameterToDelete);
    });

    this.$defs = this.$defs?.filter(def => def !== parameterToDelete);

    this.$defs?.forEach(def => {
      this.deleteNestedParameter(def, parameterToDelete);
    });
  }

  @action setDescription(description: string) {
    this.description = description;
  }

  @action setName(name: string) {
    this.name = name;
  }

  @action setStrict(strict: boolean) {
    this.strict = strict;
  }

  @action setAllowAdditionalProperties(allowAdditionalProperties: boolean) {
    this.allowAdditionalProperties = allowAdditionalProperties;
  }

  @action toggleMode() {
    if (this.mode === "interactive") {
      this.initialJSONSchema = this.definition;
    }
    this.mode = this.mode === "interactive" ? "json" : "interactive";
  }

  @action toggleAllowAdditionalProperties() {
    this.allowAdditionalProperties = !this.allowAdditionalProperties;
  }

  @action toggleStrict() {
    this.strict = !this.strict;
  }

  @action
  addParameter() {
    const newParam = new SchemaParameterStore(0, false, new SchemaParameterStore(-1));
    newParam.setDepth(0);
    this.schemaParameters.push(newParam);
  }

  private deleteNestedParameter(
    currentParam: ISchemaParameter,
    parameterToDelete: ISchemaParameter,
  ) {
    if (currentParam.children.length > 0) {
      currentParam.children = currentParam.children.filter(
        (param: ISchemaParameter) => param !== parameterToDelete,
      );

      currentParam.children.forEach((parameter: ISchemaParameter) => {
        parameter._required = parameter._required.filter(
          (requiredParam: ISchemaParameter) => requiredParam !== parameterToDelete,
        );
      });

      // Recursively check nested parameters
      currentParam.children.forEach((param: ISchemaParameter) => {
        this.deleteNestedParameter(param, parameterToDelete);
      });
    }
  }

  @action clearSchemaError() {
    this.schemaError = undefined;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applyJsonToState(jsonSchema: Record<string, any>, automatic = true) {
    if (!automatic) {
      this.initialJSONSchema = jsonSchema;
      this.isStandardSchema = false;
      this.schemaError = undefined;
      this.schemaParameters = [];
      this.$defs = undefined;
      return;
    }

    try {
      // eslint-disable-next-line no-param-reassign
      jsonSchema = stripDummyFields(jsonSchema);

      // Handle wrapper format: { name, strict, schema: {...} } or { name, parameters: {...} }
      // Extract inner schema and apply name/strict to the store
      const innerSchema = jsonSchema.schema || jsonSchema.parameters || jsonSchema.input_schema;
      const isWrapperFormat =
        innerSchema &&
        typeof innerSchema === "object" &&
        !Array.isArray(innerSchema) &&
        !("properties" in jsonSchema) &&
        ("properties" in innerSchema || innerSchema.type === "object");

      if (isWrapperFormat) {
        if (jsonSchema.name) {
          this.name = jsonSchema.name;
        }
        if (jsonSchema.description) {
          this.description = jsonSchema.description;
        }
        if (typeof jsonSchema.strict === "boolean") {
          this.strict = jsonSchema.strict;
        }
        // Extract the inner schema
        // eslint-disable-next-line no-param-reassign
        jsonSchema = innerSchema;
      }

      if (
        !validatePresetFields(jsonSchema, [...SCHEMA_EDITOR_PRESET_FIELD_KEYS]) &&
        !this.isStandardSchema
      ) {
        this.initialJSONSchema = jsonSchema;
        this.schemaError = undefined;
        return;
      } else if (!this.isStandardSchema) {
        this.isStandardSchema = true;
      }

      // Store collapsed state of existing parameters
      const expandedStateMap = new Map<string, boolean>();
      this.schemaParameters.forEach(param => {
        expandedStateMap.set(param.name, param.isExpanded);
      });

      const parent = new SchemaParameterStore(-1);

      const newParameters =
        (jsonSchema.properties &&
          Object.entries(jsonSchema.properties).map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ([key, value]: [string, any]) => {
              const param = convertJsonToParameter({ ...value }, parent);
              param.name = key;
              param.setDepth(0);
              if (param.hasComposition) {
                param._collapsed = false;
              }

              // Restore expanded state if parameter existed before
              if (expandedStateMap.has(key)) {
                if (expandedStateMap.get(key)) {
                  param._collapsed = false;
                }
              }

              return param;
            },
          )) ||
        [];

      this.schemaParameters = newParameters;
      parent.setRequired(this.schemaParameters.filter(p => jsonSchema.required?.includes(p.name)));
      const defsSource = jsonSchema.$defs ?? jsonSchema.definitions;
      if (defsSource && typeof defsSource === "object" && !Array.isArray(defsSource)) {
        this.$defs = Object.entries(defsSource).map(([key, value]) => {
          const param = convertJsonToParameter(value);
          param.name = key;
          param.setDepth(0);
          param._collapsed = false;
          return param;
        });
      } else {
        this.$defs = undefined;
      }

      this.initialJSONSchema = jsonSchema;
      this.schemaError = undefined;
    } catch (error) {
      // Preserve the malformatted schema and set an error message
      this.initialJSONSchema = jsonSchema;
      this.isStandardSchema = false;
      this.schemaError =
        error instanceof Error
          ? `Schema conversion error: ${error.message}`
          : "Schema conversion error: Unable to parse schema structure";
    }
  }

  @action addDefinition() {
    if (!this.$defs) {
      this.$defs = new Array<ISchemaParameter>();
    }
    this.$defs.push(new SchemaParameterStore(0, false));
  }

  @action deleteDefinition(definition: ISchemaParameter) {
    this.$defs = this.$defs?.filter(def => def.name !== definition.name);
  }

  /** Replace with a simple schema that is fully editable in the interactive editor. */
  @action resetToInteractiveDefault(defaultSchema: JsonSchemaRecord) {
    transaction(() => {
      this.isStandardSchema = true;
      this.schemaError = undefined;
      if (!this._isFunction && !this.name.trim()) {
        this.name = "structured_output";
      }
      this.applyJsonToState(structuredClone(defaultSchema), true);
    });
  }

  /** Soft hint for the interactive panel (union types vs load failure). */
  @computed
  get interactivePanelHint(): "composition" | "fallback" | null {
    if (!this.isStandardSchema) {
      return schemaPropertyUsesComposition(this.initialJSONSchema) ? "composition" : "fallback";
    }
    if (schemaPropertyUsesComposition(this.initialJSONSchema)) {
      return "composition";
    }
    return null;
  }

  @computed
  get hasCompositionProperties(): boolean {
    return schemaPropertyUsesComposition(this.initialJSONSchema);
  }

  /** Advanced JSON Schema (unions, $defs, or unloadable shape) — partial or JSON-only editing. */
  @computed
  get usesComplexJsonSchema(): boolean {
    return this.hasCompositionProperties || !this.isStandardSchema;
  }

  @computed
  get isUsingJinja() {
    return this.format === "jinja2";
  }

  get containsJinja(): boolean {
    return this.schemaParameters.some(param => param.containsJinja);
  }

  get definition() {
    if (!this.isStandardSchema) {
      return this.initialJSONSchema;
    }
    // Create base schema structure
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const schemaObj: Record<string, any> = {
      type: "object",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      properties: {} as Record<string, any>,
      required: [] as string[],
      additionalProperties: this.allowAdditionalProperties,
    };

    // Process each parameter (propogate additionalProperties from editor to nested parameters)
    for (const param of this.schemaParameters) {
      // Add parameter definition to properties
      const propertySchema = JSON.parse(
        JSON.stringify(param.schema).replace(
          /"additionalProperties":\s*(true|false)/g,
          `"additionalProperties":${this.allowAdditionalProperties}`,
        ),
      ) as Record<string, unknown>;
      // ``name`` is editor-internal; must not appear in JSON Schema sent to providers.

      if (propertySchema.type !== "variable") {
        delete propertySchema.name;
      }
      schemaObj.properties[param.name] = propertySchema;

      // Track required parameters
      if (param.required) {
        schemaObj.required.push(param.name);
      }
    }

    if (this.$defs && this.$defs.length > 0) {
      const defsKey =
        this.initialJSONSchema &&
        typeof this.initialJSONSchema === "object" &&
        !Array.isArray(this.initialJSONSchema) &&
        Object.prototype.hasOwnProperty.call(this.initialJSONSchema, "definitions") &&
        !Object.prototype.hasOwnProperty.call(this.initialJSONSchema, "$defs")
          ? "definitions"
          : "$defs";
      schemaObj[defsKey] = this.$defs.reduce(
        (defs, def) => {
          defs[def.name] = JSON.parse(JSON.stringify(def.schema));
          return defs;
        },
        {} as Record<string, ISchemaParameter["schema"]>,
      );
    }
    return mergePreservedRootSchemaFields(
      schemaObj,
      this.initialJSONSchema as Record<string, unknown>,
    );
  }
}

export default SchemaEditorStore;
